import type { DraftJobViewModel } from '@/features/jobs/drafting/DraftJobViewModel'
import { observer } from 'mobx-react-lite'
import { useId } from 'react'
import { useObservableValue } from '@/hooks/rx-hooks'
import {
  CheckoutViewBillingFormSection,
  CheckoutViewBillingMethodsSection,
  CheckoutViewCouponCodeInputField,
  CheckoutViewCouponSection,
  CheckoutViewSectionSeparator,
  CheckoutViewCouponSectionAvailableCouponsList,
  CheckoutViewCouponSectionAvailableCouponsListItem,
  CheckoutViewCouponSectionAppliedCoupon,
} from '@/features/jobs/drafting/checkout/components/CheckoutView/index'
import {
  BillingMethodList,
  BillingMethodListItemSkeleton,
} from '@/features/billing/profile/BillingMethodList'
import { CardBillingMethodEditor } from '@/features/billing/profile/CardBillingMethodEditor'
import { BillingMethodListContainer } from '@/features/billing/profile/BillingMethodList/BillingMethodListContainer'
import { ApplePayButtonContainer } from '@/features/billing/components/ApplePayButton'
import { bindInput } from '@/infra/view-binding'
import type { Coupon, CouponOffType } from '@/features/billing/coupons/Coupon'
import { match } from '@/utils/pattern-match-util'
import { formatMoney } from '@/utils/money-util'
import type { Observable } from 'rxjs'
import type { ColorScheme } from '@/features/shell/theming'

interface CheckoutBillingSectionContainerProps {
  vm: DraftJobViewModel
  colorScheme$: Observable<ColorScheme>
  formId?: string | null
}

/**
 * Checkout Billing Section Container
 */
export const CheckoutBillingSectionContainer = observer(
  function CheckoutBillingSectionContainer({
    formId,
    colorScheme$,
    vm: { checkoutVm: vm },
  }: CheckoutBillingSectionContainerProps) {
    const colorScheme = useObservableValue(colorScheme$, {
      initial: 'light' as const,
    })

    const titleId = useId()
    const isDisabled = vm.submit.pending

    if (!vm.billingVm.isBillingEnabled) {
      return null
    }

    if (vm.billingVm.isFetchingBillingInfo) {
      return (
        <CheckoutViewBillingMethodsSection isSkeleton isDisabled>
          <BillingMethodList aria-label="Placeholder list">
            <BillingMethodListItemSkeleton />
          </BillingMethodList>
        </CheckoutViewBillingMethodsSection>
      )
    }

    if (vm.billingVm.cardBillingMethodEditorVm && formId) {
      return (
        <>
          {vm.billingVm.isApplePayAvailable && (
            <>
              <ApplePayButtonContainer
                vm={vm.billingVm.applePayVm}
                colorScheme={colorScheme}
              />
              <CheckoutViewSectionSeparator
                label="Or"
                aria-label="Choose other payment methods"
                padded
              />
            </>
          )}
          <CheckoutViewBillingFormSection>
            <CardBillingMethodEditor
              vm={vm.billingVm.cardBillingMethodEditorVm}
              onSubmit={vm.submit}
              isDisabled={isDisabled}
              formId={formId}
            />
          </CheckoutViewBillingFormSection>
        </>
      )
    }

    return (
      <>
        <CheckoutViewBillingMethodsSection
          titleId={titleId}
          isDisabled={isDisabled}
          onAddMethodPress={vm.billingVm.showNewBillingMethodDialog}
        >
          <BillingMethodListContainer
            vm={vm.billingVm.billingMethodListVm}
            aria-labelledby={titleId}
            isDisabled={isDisabled}
            mode="select"
          />
        </CheckoutViewBillingMethodsSection>

        {vm.billingVm.isApplePayAvailable && (
          <>
            <CheckoutViewSectionSeparator
              label="Or"
              aria-label="Choose other payment methods"
              padded
            />
            <ApplePayButtonContainer
              vm={vm.billingVm.applePayVm}
              colorScheme={colorScheme}
            />
          </>
        )}
      </>
    )
  },
)

interface CheckoutViewCouponSectionContainerProps {
  vm: DraftJobViewModel
}

/**
 * Checkout View Coupon Section Container
 */
export const CheckoutViewCouponSectionContainer = observer(
  function CheckoutViewCouponSectionContainer({
    vm: { checkoutVm: checkoutVm },
  }: CheckoutViewCouponSectionContainerProps) {
    const { billingVm: vm } = checkoutVm

    if (!vm.isCouponsFeatureEnabled) {
      return null
    }

    const otherCoupons = vm.availableCoupons.filter(
      (coupon) => coupon.id !== vm.selectedCoupon?.id,
    )

    const isDisabled = checkoutVm.submit.pending

    return (
      <CheckoutViewCouponSection
        isSkeleton={vm.isFetchingCoupons}
        title={
          !otherCoupons.length || vm.selectedCoupon
            ? 'Coupon'
            : 'Available coupons'
        }
      >
        {vm.selectedCoupon && (
          <>
            <CheckoutViewCouponSectionAppliedCoupon
              {...formatCouponProps(vm.selectedCoupon)}
              onRemovePress={vm.removeCoupon}
              isDisabled={vm.isCouponActionPending || isDisabled}
              isExpired={vm.selectedCoupon.isExpired}
            />
            <CheckoutViewSectionSeparator
              label={otherCoupons.length > 0 ? 'Other coupons' : 'Or'}
              aria-label={
                otherCoupons.length > 0 ? 'Other coupons' : 'Add a coupon'
              }
            />
          </>
        )}

        {otherCoupons.length > 0 && (
          <>
            <CheckoutViewCouponSectionAvailableCouponsList
              aria-label={
                vm.selectedCoupon ? 'Other coupons' : 'Available coupons'
              }
            >
              {otherCoupons.map((coupon) => {
                // Whether this coupon is in the process of being applied.
                const isApplyPendingForThis =
                  vm.applyCoupon.pending && vm.applyCoupon.args[0] === coupon
                return (
                  <CheckoutViewCouponSectionAvailableCouponsListItem
                    key={coupon.id}
                    {...formatCouponProps(coupon)}
                    onApplyPress={() => void vm.applyCoupon(coupon)}
                    isPending={isApplyPendingForThis}
                    testId={`available-coupon-${coupon.code}`}
                    applyButtonTestId={`available-coupon-apply-button-${coupon.code}`}
                    isDisabled={
                      isDisabled ||
                      (vm.isCouponActionPending && !isApplyPendingForThis)
                    }
                  />
                )
              })}
            </CheckoutViewCouponSectionAvailableCouponsList>
            <CheckoutViewSectionSeparator
              label="Or"
              aria-label="Add a coupon"
            />
          </>
        )}

        <CheckoutViewCouponCodeInputField
          onAddPress={vm.addNewCoupon}
          isPending={vm.addNewCoupon.pending}
          isSkeleton={vm.isFetchingCoupons}
          isDisabled={
            isDisabled || (vm.isCouponActionPending && !vm.addNewCoupon.pending)
          }
          inputTestId="coupon-code-input"
          applyButtonTestId="coupon-code-apply-button"
          {...bindInput(vm, 'newCouponCode')}
        />
      </CheckoutViewCouponSection>
    )
  },
)

function formatCouponProps(coupon: Coupon) {
  if (coupon.description) {
    return {
      title: formatCouponTitle(coupon),
      description: coupon.description,
    }
  }

  return {
    title: formatOffType(coupon.offType),
    description: coupon.name,
  }
}

function formatCouponTitle(coupon: Coupon) {
  return `${coupon.name} – ${formatOffType(coupon.offType)}`
}

function formatOffType(offType: CouponOffType) {
  return match(offType, 'type', {
    Amount: ({ amount }) => `${formatMoney(amount)} off`,
    Percent: ({ percent }) => `${percent}% off`,
  })
}
